import { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  SESSION_QUERY_KEY,
  setCsrfToken,
  setCurrentOrgSlug,
  setUnauthorizedHandler,
} from "./client";
import { postLogout, subscribe } from "./authChannel";
import type { SessionResponse } from "../auth/types";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

type ExpireSource = "local" | "remote";

function markSessionExpired(source: ExpireSource): void {
  const current = queryClient.getQueryData<SessionResponse>(SESSION_QUERY_KEY);
  if (current?.authenticated === false) return;

  // Preserve `user`, `currentOrgSlug`, `csrfToken` etc. so consumers reading
  // those between this flip and the redirect don't suddenly see undefined.
  // `isAuthenticated` in useAuth keys off `authenticated === true && user`,
  // so flipping `authenticated` is enough to trigger the route guard.
  queryClient.setQueryData<SessionResponse>(SESSION_QUERY_KEY, (prev) =>
    prev ? { ...prev, authenticated: false } : { authenticated: false },
  );

  // Stale module-state would otherwise live until the next full reload, and
  // an in-flight `orgPath(...)` call after the slug clears would throw.
  setCsrfToken(null);
  setCurrentOrgSlug(null);

  toast.error(
    source === "local"
      ? "Your session expired. Please sign in again."
      : "You were signed out in another tab.",
    { id: "session-expired" },
  );

  if (source === "local") postLogout();
}

setUnauthorizedHandler(() => markSessionExpired("local"));
subscribe((event) => {
  if (event.type === "logout") markSessionExpired("remote");
});
